/* eslint-disable no-unused-vars */

/* eslint-disable react/prop-types */

/* eslint-disable react/display-name */
import { format, isValid, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { STATEMENT_STATUSES } from '_constants/statements';
import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';

import Icon from 'components/atomic/Icon';
import UploadButton from 'components/atomic/UploadButton';
import Text from 'components/atomic/layouts/Text';

const StyledContainer = styled.div`
  padding: ${theme.spaces.regular}px;
  border-radius: 8px;
  border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
`;

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${theme.spaces.regular}px;
  padding-bottom: ${theme.spaces.regular}px;
  border-bottom: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};

  &:last-child {
    border-bottom: unset;
  }

  > div:last-child {
    display: flex;
    justify-content: space-between;
  }

  @media ${theme.devices.tablet} {
    > div:last-child {
      flex-direction: column;
      align-items: flex-start;
      gap: ${theme.spaces.smallX}px;
    }
  }
`;

const StyledStatusContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
`;

const StyledStatusIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${theme.spaces.smallX}px;

  @media ${theme.devices.tablet} {
    justify-content: flex-start;
  }
`;

const StyledActionButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 26px;
  padding-top: ${theme.spaces.small2X - 2}px;
  padding-bottom: ${theme.spaces.small2X - 2}px;
  padding-right: ${theme.spaces.small2X}px;
  padding-left: ${theme.spaces.small2X}px;
  border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
  border-radius: 8px;
  background: ${hexToRGBA(theme.colors.gray.secondary, 0.08)};
  cursor: pointer;

  &:hover {
    border: 1px solid ${hexToRGBA(theme.colors.indigo.primary, 0.16)};
    background: ${hexToRGBA(theme.colors.indigo.primary, 0.08)};

    * {
      color: ${theme.colors.indigo.primary};
    }

    svg {
      path {
        stroke: ${theme.colors.indigo.primary};
      }
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      opacity: 0.4;
    `}
`;

const StyledFileNameContainer = styled.div`
  width: 100%;

  > * {
    white-space: pre-wrap;
    word-wrap: break-word;
  }
`;

const DocList = ({ fileTypeMap = {}, loading, items = [], onDownload, onUpload, onDelete }) => {
  const handleDownload = (id, fileName) => () => {
    onDownload && onDownload(id, fileName);
  };

  const handleUpload = (id) => (file) => {
    onUpload && onUpload(id, file);
  };

  const handleDelete = (id) => () => {
    onDelete && onDelete(id);
  };

  if (!items.length && loading) {
    return (
      <StyledContainer>
        <Text>Loading...</Text>
      </StyledContainer>
    );
  }

  if (!items.length) {
    return (
      <StyledContainer>
        <Text>No data available</Text>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <Text size={theme.fontSizes.regular} weight={600} bottomSpace={theme.spaces.smallX}>
        Your Uploaded Documents
      </Text>
      <StyledBody>
        {items.map(({ id, type, year, status, fileName, updated }) => {
          const parsedDate = parseISO(updated);
          return (
            <StyledItemContainer key={id}>
              <div>
                <Text
                  size={theme.fontSizes.small}
                  weight={600}
                  color={theme.colors.gray.primary}
                  bottomSpace={theme.spaces.regular}
                >
                  Year: {year || 'N/A'}
                </Text>
                <Text
                  size={theme.fontSizes.small}
                  weight={400}
                  color={theme.colors.gray.primary}
                  bottomSpace={theme.spaces.smallX}
                >
                  Type: {fileTypeMap[type] || type?.replace(/([A-Z])/g, ' $1').trim()}
                </Text>
                <StyledFileNameContainer>
                  <Text
                    size={theme.fontSizes.small}
                    weight={400}
                    color={theme.colors.gray.primary}
                    bottomSpace={theme.spaces.smallX}
                  >
                    File: {fileName || 'N/A'}
                  </Text>
                </StyledFileNameContainer>

                <Text
                  size={theme.fontSizes.small}
                  weight={400}
                  color={theme.colors.gray.primary}
                  bottomSpace={theme.spaces.regular}
                >
                  Date Updated: {isValid(parsedDate) ? format(parsedDate, 'MM/dd/yyyy') : updated}
                </Text>
              </div>
              <div>
                <StyledStatusContainer>
                  <StyledStatusIconContainer>
                    <Icon
                      alias={
                        status !== STATEMENT_STATUSES.requested ? ICON_ALIASES.tickCircleFilled : ICON_ALIASES.slash
                      }
                      color={
                        status !== STATEMENT_STATUSES.requested
                          ? theme.colors.indigo.primary
                          : theme.colors.gray.secondary
                      }
                    />
                  </StyledStatusIconContainer>
                  <Text size={theme.fontSizes.small} weight={600} color={theme.colors.gray.secondary}>
                    {status === STATEMENT_STATUSES.requested && 'Not Uploaded'}
                    {status === STATEMENT_STATUSES.underReview && 'Uploaded'}
                    {status === STATEMENT_STATUSES.confirmed && 'Reviewed'}
                  </Text>
                </StyledStatusContainer>
                <StyledActionsContainer>
                  <StyledActionButton
                    disabled={status === STATEMENT_STATUSES.requested}
                    onClick={handleDownload(id, fileName)}
                  >
                    <Icon alias={ICON_ALIASES.arrowDownCircle} color={theme.colors.gray.secondary} />
                  </StyledActionButton>
                  {status === STATEMENT_STATUSES.requested && (
                    <UploadButton
                      renderAs={(props) => (
                        <StyledActionButton {...props}>
                          <Text size={theme.fontSizes.smallX} weight={600} color={theme.colors.gray.secondary}>
                            Add File
                          </Text>
                        </StyledActionButton>
                      )}
                      onChange={handleUpload(id)}
                    />
                  )}
                  {status !== STATEMENT_STATUSES.requested && (
                    <UploadButton
                      renderAs={(props) => (
                        <StyledActionButton {...props}>
                          <Text size={theme.fontSizes.smallX} weight={600} color={theme.colors.gray.secondary}>
                            Update File
                          </Text>
                        </StyledActionButton>
                      )}
                      onChange={handleUpload(id)}
                    />
                  )}
                  <StyledActionButton onClick={handleDelete(id)}>
                    <Text size={theme.fontSizes.smallX} weight={600} color={theme.colors.gray.secondary}>
                      Delete
                    </Text>
                  </StyledActionButton>
                </StyledActionsContainer>
              </div>
            </StyledItemContainer>
          );
        })}
      </StyledBody>
    </StyledContainer>
  );
};

DocList.propTypes = {
  fileTypeMap: PropTypes.object,
  loading: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
      status: PropTypes.string,
      year: PropTypes.string,
      fileName: PropTypes.string,
      updated: PropTypes.string,
    })
  ),
  onDownload: PropTypes.func,
  onUpload: PropTypes.func,
  onDelete: PropTypes.func,
};

export default DocList;
